import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, TrendingUp, TrendingDown, Minus, Sparkles, Compass } from "lucide-react";
import client from "../lib/api";
import { LAYER_ORDER } from "../components/RadarChart";

/**
 * Delta screen. Shown after a check in with Klair. Presents the
 * meaningful shifts against the last read in plain language.
 */
export default function DeltaScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [delta, setDelta] = useState(null);
  const [times, setTimes] = useState({ current: null, previous: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const cached = localStorage.getItem("klareet.report.delta");
        if (cached) {
          setDelta(JSON.parse(cached));
        }
        const { data } = await client.get(`/demo/read/delta?product=${product}`);
        if (data?.delta) setDelta(data.delta);
        setTimes({ current: data?.current, previous: data?.previous });
      } catch (_e) { /* ignore */ }
      setLoading(false);
    })();
  }, [product]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-16 text-center">
        <p className="k-quiet">Reading your check in.</p>
      </div>
    );
  }
  if (!delta) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-16 text-center">
        <p className="k-quiet">No check in yet.</p>
        <Link to={`${base}/home`} className="k-btn k-btn-primary mt-4 inline-flex">Back to Home</Link>
      </div>
    );
  }

  const layerByKey = (name) => {
    const key = (name || "").toLowerCase();
    return LAYER_ORDER.find((L) => key.includes(L.key)) || LAYER_ORDER[0];
  };

  return (
    <div className="relative max-w-4xl mx-auto px-5 pt-8 pb-24">
      <div className="k-orb k-orb-teal"  style={{ top: "-6rem", right: "-8rem", width: "20rem", height: "20rem" }} />
      <div className="k-orb k-orb-amber" style={{ bottom: "-4rem", left: "10%",  width: "16rem", height: "16rem" }} />

      <div className="k-fade-in">
        <div className="k-chip k-chip-teal mb-3"><Sparkles size={12} /> check in complete</div>
        <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(1.9rem, 4vw, 2.6rem)", lineHeight: 1.1 }} data-testid="delta-title">
          What has shifted since last time
        </h1>
        <p className="k-hero-sub mb-8" data-testid="delta-summary">{delta.summary}</p>
      </div>

      {/* Archetype card if changed */}
      {delta.archetype_changed && delta.archetype?.previous?.primary && (
        <div className="k-card mb-8 k-fade-in" style={{ animationDelay: "60ms" }} data-testid="delta-archetype">
          <div className="k-label mb-3">archetype shift</div>
          <div className="flex flex-wrap items-center gap-4">
            <ArchetypeSquare arch={delta.archetype.previous.primary} tone="ghost" label="was" />
            <ArrowRight size={20} style={{ color: "hsl(var(--klareet-muted))" }} />
            <ArchetypeSquare arch={delta.archetype.current.primary} tone="live" label="now" />
          </div>
        </div>
      )}

      {/* Cell shift */}
      {delta.cell?.moved && (
        <div className="k-card mb-8" data-testid="delta-cell">
          <div className="k-label mb-3">where you stand</div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-sm k-quiet">was</div>
            <div className="klareet-serif" style={{ fontSize: "1.1rem" }}>{delta.cell.previous?.quadrant_or_cell || "not set"}</div>
            <ArrowRight size={16} style={{ color: "hsl(var(--klareet-muted))" }} />
            <div className="text-sm k-quiet">now</div>
            <div className="klareet-serif font-semibold" style={{ fontSize: "1.1rem", color: "hsl(var(--klareet-blue-strong))" }}>
              {delta.cell.current?.quadrant_or_cell}
            </div>
          </div>
        </div>
      )}

      {/* Layer deltas */}
      <div className="k-card mb-8" data-testid="delta-layers">
        <div className="k-label mb-4">your seven sides, then and now</div>
        <ul className="space-y-3">
          {delta.layer_deltas.map((ld) => {
            const layer = layerByKey(ld.layer);
            const icon = ld.direction === "up" ? <TrendingUp size={14} />
                       : ld.direction === "down" ? <TrendingDown size={14} />
                       : <Minus size={14} />;
            const color = ld.direction === "up"   ? "hsl(var(--klareet-teal))"
                       : ld.direction === "down" ? "hsl(var(--klareet-red))"
                       : "hsl(var(--klareet-muted))";
            const pct = Math.max(0, Math.min(100, Math.round((ld.current / 5) * 100)));
            const prevPct = Math.max(0, Math.min(100, Math.round((ld.previous / 5) * 100)));
            return (
              <li key={ld.layer} className="p-3 rounded-lg" style={{
                border: "1px solid hsl(var(--klareet-line-soft))",
                background: `linear-gradient(90deg, hsl(var(--k-${layer.hue}) / 0.05), transparent 65%)`,
              }} data-testid={`delta-layer-${layer.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: `hsl(var(--k-${layer.hue}))` }} />
                    <span className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>{ld.layer}</span>
                  </div>
                  <div className="flex items-center gap-1.5 klareet-mono text-sm" style={{ color }}>
                    {icon}
                    <span>{ld.delta_label}</span>
                  </div>
                </div>
                <div className="relative k-progress-track">
                  <div className="k-progress-bar" style={{ width: `${prevPct}%`, opacity: 0.35, background: "hsl(var(--klareet-muted))" }} />
                  <div className="k-progress-bar" style={{ position: "absolute", top: 0, left: 0, width: `${pct}%`, background: `hsl(var(--k-${layer.hue}))` }} />
                </div>
                <div className="flex justify-between mt-1 klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-muted))" }}>
                  <span>was {ld.previous.toFixed(2)}</span>
                  <span>now {ld.current.toFixed(2)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Honest line comparison */}
      {delta.honest_line?.current && (
        <div className="k-warm-card p-6 mb-8" data-testid="delta-honest">
          <div className="k-label mb-2">the straight version, updated</div>
          <p className="klareet-serif" style={{ fontSize: "1.15rem", lineHeight: 1.4 }}>
            {delta.honest_line.current}
          </p>
          {delta.honest_line.previous && delta.honest_line.previous !== delta.honest_line.current && (
            <p className="k-quiet mt-3 italic">was: {delta.honest_line.previous}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button onClick={() => nav(`${base}/report`)} className="k-btn k-btn-accent" data-testid="delta-see-full">
          <Compass size={14} /> Open the full read
        </button>
        <button onClick={() => nav(`${base}/home`)} className="k-btn k-btn-ghost" data-testid="delta-back-home">
          Back to Home
        </button>
      </div>
    </div>
  );
}

function ArchetypeSquare({ arch, tone, label }) {
  return (
    <div className="p-3 rounded-lg min-w-[10rem]" style={{
      border: tone === "live" ? "1px solid hsl(var(--klareet-blue))" : "1px dashed hsl(var(--klareet-line))",
      background: tone === "live" ? "hsl(var(--klareet-blue-soft))" : "hsl(var(--klareet-canvas-2))",
    }}>
      <div className="k-quiet !text-[10px] uppercase klareet-mono">{label}</div>
      <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{arch.name}</div>
      <div className="k-quiet !text-xs">{arch.tag}</div>
    </div>
  );
}
